import { Card } from '../components/ui/card';
import { Shield, Lock, Eye, Database, UserCheck, Bell } from 'lucide-react';

export const Privacy = () => {
  const sections = [
    {
      icon: <Database className="w-6 h-6" />,
      title: 'Information We Collect',
      content: `We collect information you provide directly to us, such as when you create an account, place an order, or contact us. This includes:
      • Personal information (name, email, phone number, address)
      • Payment information (processed securely through our payment partners)
      • Order history and preferences
      • Device and usage information
      • Location data (when you permit)`,
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'How We Use Your Information',
      content: `We use the information we collect to:
      • Process and deliver your orders
      • Send order confirmations and updates
      • Provide customer support
      • Personalize your experience and recommendations
      • Improve our services and develop new features
      • Send promotional communications (with your consent)
      • Ensure platform security and prevent fraud`,
    },
    {
      icon: <UserCheck className="w-6 h-6" />,
      title: 'Information Sharing',
      content: `We may share your information with:
      • Restaurant partners (to fulfill your orders)
      • Delivery partners (to deliver your orders)
      • Payment processors (to process transactions)
      • Service providers (who assist our operations)
      We never sell your personal information to third parties.`,
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Data Security',
      content: `We implement industry-standard security measures including:
      • Encryption of data in transit and at rest
      • Regular security audits and assessments
      • Access controls and authentication
      • Secure data centers with 24/7 monitoring`,
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Your Rights',
      content: `You have the right to:
      • Access your personal data
      • Correct inaccurate information
      • Delete your account and data
      • Opt-out of marketing communications
      • Export your data in a portable format`,
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: 'Updates to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of any significant changes through the app or via email. Your continued use of our services after such modifications constitutes acceptance of the updated policy.`,
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="privacy-page">
      {/* Hero */}
      <section className="fudco-hero-gradient fudco-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-[#2D8B2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Legal
          </div>
          <h1 className="fudco-heading-1 text-gray-900 mb-6">
            Privacy <span className="text-[#2D8B2E]">Policy</span>
          </h1>
          <p className="fudco-body-lg">
            Last updated: January 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="fudco-section bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 mb-8">
            <p className="fudco-body-lg mb-6">
              At Fudco, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. Please read this policy carefully.
            </p>
            <p className="text-gray-600">
              By using Fudco, you agree to the collection and use of information in accordance with this policy.
            </p>
          </Card>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="p-8" data-testid={`privacy-section-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-green-100 text-[#2D8B2E] p-3 rounded-xl">
                    {section.icon}
                  </div>
                  <h2 className="fudco-heading-3 text-gray-900">{section.title}</h2>
                </div>
                <div className="text-gray-600 whitespace-pre-line">{section.content}</div>
              </Card>
            ))}
          </div>

          <Card className="p-8 mt-8 bg-green-50 border-green-100">
            <h3 className="fudco-heading-3 text-gray-900 mb-4">Contact Us</h3>
            <p className="text-gray-600">
              If you have any questions about this Privacy Policy, please contact us at:
              <br />
              <strong className="text-[#2D8B2E]">service@fudco.in</strong>
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};
